import { chapters, TranscriptData } from './data/chapters';
import { AudioManager } from './audio/AudioManager';
import { FractalRenderer } from './visuals/FractalRenderer';
import { WordSync } from './typography/WordSync';

type ExperienceState = 'welcome' | 'intro' | 'playing' | 'paused' | 'waiting' | 'transitioning' | 'complete';

export class Experience {
  private container: HTMLElement;
  private state: ExperienceState = 'welcome';
  private currentChapterIndex: number = 0;
  
  private audioManager: AudioManager | null = null;
  private fractalRenderer: FractalRenderer | null = null;
  private wordSync: WordSync | null = null;
  
  private welcomeScreen: HTMLElement | null = null;
  private continueContainer: HTMLElement | null = null;
  private chapterTitle: HTMLElement | null = null;
  private canvasContainer: HTMLElement | null = null;
  
  private animationFrameId: number | null = null;
  private introStartTime: number = 0;
  
  // Wake Lock to prevent screen from sleeping on mobile
  private wakeLock: WakeLockSentinel | null = null;
  
  constructor(container: HTMLElement) {
    this.container = container;
    this.createDOM();
    this.bindEvents();
  }
  
  private createDOM(): void {
    this.container.innerHTML = `
      <div id="canvas-container"></div>
      
      <div id="welcome-screen">
        <h1 class="welcome-title">Materia Fraktalna</h1>
        <button id="enter-btn">Wchodzę</button>
      </div>
      
      <div id="chapter-title"></div>
      
      <div id="typography-layer">
        <div class="text-container" id="text-container"></div>
      </div>
      
      <div id="continue-container">
        <p class="interaction-hint">Przeciągnij aby eksplorować • Scroll/Pinch aby przybliżać • Klik/Tap aby zbliżyć</p>
        <button id="continue-btn">Kontynuuj</button>
      </div>
    `;
    
    this.welcomeScreen = document.getElementById('welcome-screen');
    this.continueContainer = document.getElementById('continue-container');
    this.chapterTitle = document.getElementById('chapter-title');
    this.canvasContainer = document.getElementById('canvas-container');
  }
  
  private bindEvents(): void {
    const enterBtn = document.getElementById('enter-btn');
    const continueBtn = document.getElementById('continue-btn');
    
    enterBtn?.addEventListener('click', () => this.start());
    continueBtn?.addEventListener('click', () => this.continueToNextChapter());
  }
  
  private async start(): Promise<void> {
    if (this.state !== 'welcome') return;
    
    console.log('Starting experience...');
    
    // Initialize audio context on user interaction (required for iOS)
    this.audioManager = new AudioManager();
    try {
      await this.audioManager.init();
      // Force resume on iOS - must be in direct user interaction chain
      await this.audioManager.forceResume();
    } catch (e) {
      console.log('Audio init error, retrying...', e);
      // Retry once on iOS
      await new Promise(r => setTimeout(r, 100));
      await this.audioManager.init();
      await this.audioManager.forceResume();
    }
    
    console.log('Audio initialized');
    
    // Initialize fractal renderer
    const canvasContainer = document.getElementById('canvas-container');
    if (canvasContainer) {
      this.fractalRenderer = new FractalRenderer(canvasContainer);
    }
    
    // Initialize word sync
    const textContainer = document.getElementById('text-container');
    if (textContainer) {
      this.wordSync = new WordSync(textContainer);
    }
    
    // Hide welcome screen
    this.welcomeScreen?.classList.add('hidden');
    
    // Request wake lock to prevent screen sleep on mobile
    await this.requestWakeLock();
    
    // Start intro sequence
    this.state = 'intro';
    this.introStartTime = performance.now();
    
    // Start the first chapter's music with fade in
    const firstChapter = chapters[0];
    await this.audioManager.loadMusic(firstChapter.musicPath);
    await this.audioManager.playMusic(0, true); // Start with fade in
    
    // CRITICAL FOR iOS: Preload and unlock voiceover during user interaction
    await this.audioManager.loadVoiceover(firstChapter.voiceoverPath);
    await this.audioManager.unlockVoiceover();
    
    // Set initial fractal
    this.fractalRenderer?.setFractalType(firstChapter.fractalType, firstChapter.colorScheme);
    
    // Start animation loop
    this.animate();
    
    // Slowly reveal fractal (starts dark, fades in over 12 seconds)
    if (this.canvasContainer) {
      this.canvasContainer.style.opacity = '0';
      this.canvasContainer.style.transition = 'opacity 12s ease-out';
      // Trigger the transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.canvasContainer) {
            this.canvasContainer.style.opacity = '1';
          }
        });
      });
    }
    
    // After 10 seconds, start the voiceover
    setTimeout(() => {
      this.startChapter(0);
    }, 10000);
  }
  
  private async startChapter(index: number): Promise<void> {
    // This is used only for the first chapter after the 10s intro
    if (index >= chapters.length) {
      this.state = 'complete';
      return;
    }
    
    this.currentChapterIndex = index;
    const chapter = chapters[index];
    
    this.state = 'playing';
    
    // Update chapter title
    if (this.chapterTitle) {
      this.chapterTitle.textContent = chapter.title;
      this.chapterTitle.classList.add('visible');
    }
    
    // Load and set transcript
    const transcript = await this.loadTranscript(chapter.transcriptPath);
    if (transcript && this.wordSync) {
      this.wordSync.setTranscript(transcript);
    }
    
    // Play voiceover - already loaded and unlocked in start()
    // Just play it now
    await this.audioManager?.playVoiceover();
    
    // Listen for voiceover end
    this.audioManager?.onVoiceoverEnd(() => {
      this.onChapterEnd();
    });
  }
  
  private async loadTranscript(path: string): Promise<TranscriptData | null> {
    try {
      const response = await fetch(path);
      return await response.json();
    } catch (error) {
      console.error('Failed to load transcript:', error);
      return null;
    }
  }
  
  private async onChapterEnd(): Promise<void> {
    this.state = 'waiting';
    
    // Fade out chapter title smoothly
    this.chapterTitle?.classList.add('fading');
    setTimeout(() => {
      this.chapterTitle?.classList.remove('visible', 'fading', 'slow-fade');
    }, 1500);
    
    // Fade out words with animation
    await this.wordSync?.fadeOut(2000);
    
    // Enable fractal interaction during pause
    this.fractalRenderer?.enableInteraction();
    
    // Show continue button after words have faded
    setTimeout(() => {
      this.continueContainer?.classList.add('visible');
    }, 1000);
  }
  
  private async continueToNextChapter(): Promise<void> {
    if (this.state !== 'waiting') {
      console.log('continueToNextChapter: state is not waiting, current state:', this.state);
      return;
    }
    
    console.log('continueToNextChapter: starting transition from chapter', this.currentChapterIndex);
    this.state = 'transitioning';
    
    // Hide continue button with fade
    this.continueContainer?.classList.remove('visible');
    
    // Disable interaction (keep current zoom position)
    this.fractalRenderer?.disableInteraction();
    
    let nextIndex = this.currentChapterIndex + 1;
    
    // Loop back to first chapter at the end
    if (nextIndex >= chapters.length) {
      nextIndex = 0;
    }
    
    console.log('continueToNextChapter: going to chapter', nextIndex);
    
    // Get next chapter
    const nextChapter = chapters[nextIndex];
    
    try {
      // Force resume audio context (iOS - must be in user interaction chain)
      await this.audioManager?.forceResume();
      
      // CRITICAL FOR iOS: Load and unlock voiceover NOW during user interaction
      console.log('Loading voiceover:', nextChapter.voiceoverPath);
      await this.audioManager?.loadVoiceover(nextChapter.voiceoverPath);
      await this.audioManager?.unlockVoiceover();
      
      // Start music crossfade IMMEDIATELY (10 seconds - shorter for better flow)
      console.log('Loading music:', nextChapter.musicPath);
      await this.audioManager?.loadMusic(nextChapter.musicPath);
      this.audioManager?.crossfadeToNextMusic(10000);
    } catch (e) {
      console.error('Error loading audio for next chapter:', e);
    }
    
    // DON'T reset zoom - keep user's current view
    // Crossfade to new fractal with opacity transition
    console.log('Crossfading to fractal:', nextChapter.fractalType);
    this.fractalRenderer?.crossfadeToFractal(nextChapter.fractalType, nextChapter.colorScheme, 3000);
    
    // Show chapter title after 2 seconds
    setTimeout(() => {
      if (this.chapterTitle) {
        this.chapterTitle.textContent = nextChapter.title;
        this.chapterTitle.classList.add('visible', 'slow-fade');
      }
    }, 2000);
    
    // Start voiceover after 4 seconds (music already flowing, title appearing)
    setTimeout(() => {
      console.log('Starting voiceover for chapter', nextIndex);
      this.startChapterVoiceover(nextIndex);
    }, 4000);
  }
  
  private async startChapterVoiceover(index: number): Promise<void> {
    const chapter = chapters[index];
    this.currentChapterIndex = index;
    this.state = 'playing';
    
    // Title was already set in continueToNextChapter, just ensure it's visible
    if (this.chapterTitle) {
      this.chapterTitle.classList.remove('slow-fade');
    }
    
    // Load and set transcript
    const transcript = await this.loadTranscript(chapter.transcriptPath);
    if (transcript && this.wordSync) {
      this.wordSync.setTranscript(transcript);
    }
    
    // Play voiceover - already loaded and unlocked in continueToNextChapter
    await this.audioManager?.playVoiceover();
    
    // Listen for voiceover end
    this.audioManager?.onVoiceoverEnd(() => {
      this.onChapterEnd();
    });
  }

  private animate = (): void => {
    this.animationFrameId = requestAnimationFrame(this.animate);
    
    const now = performance.now();
    
    // Get audio analysis data
    const audioData = this.audioManager?.getAnalysisData() || {
      bass: 0,
      mid: 0,
      high: 0,
      average: 0
    };
    
    // Update fractal with audio reactivity
    if (this.fractalRenderer) {
      const elapsed = (now - this.introStartTime) / 1000;
      this.fractalRenderer.update(elapsed, audioData);
      this.fractalRenderer.render();
    }
    
    // Update word sync
    if (this.state === 'playing' && this.wordSync && this.audioManager) {
      const voiceoverTime = this.audioManager.getVoiceoverTime();
      this.wordSync.update(voiceoverTime);
    }
  };
  
  public pause(): void {
    if (this.state === 'playing') {
      this.audioManager?.pause();
      this.state = 'paused';
    }
  }
  
  public resume(): void {
    if (this.state === 'paused') {
      this.audioManager?.resume();
      this.state = 'playing';
    }
  }
  
  public resize(): void {
    this.fractalRenderer?.resize();
  }
  
  public destroy(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.audioManager?.destroy();
    this.fractalRenderer?.destroy();
    this.releaseWakeLock();
  }
  
  // Prevent screen from sleeping on mobile devices
  private async requestWakeLock(): Promise<void> {
    if ('wakeLock' in navigator) {
      try {
        this.wakeLock = await navigator.wakeLock.request('screen');
        console.log('Wake Lock active - screen will stay on');
        
        // Re-request wake lock if it's released (e.g., tab switch)
        this.wakeLock.addEventListener('release', () => {
          console.log('Wake Lock released');
        });
      } catch (err) {
        console.log('Wake Lock not available:', err);
      }
    }
  }
  
  private releaseWakeLock(): void {
    if (this.wakeLock) {
      this.wakeLock.release();
      this.wakeLock = null;
    }
  }
  
  // Re-acquire wake lock when page becomes visible again
  public async handleVisibilityChange(): Promise<void> {
    if (document.visibilityState === 'visible' && this.state !== 'welcome') {
      await this.requestWakeLock();
    }
  }
}

